import i18n from "../../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Group,
  SimpleGrid,
  Paper,
  Text,
  Table,
  Stack,
  Tabs,
  Title,
} from "@mantine/core";
import moment from "moment";
import { Page } from "../../layout/page";
import { useServerApi } from "../../hooks/userServerApi";
import { useQuizBasePath } from "../../hooks/useQuizBasePath";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { useCellRender } from "../../hooks/useCellRender";
import QuizStatusBadge from "../../components/quiz/QuizStatusBadge";
import { McaEntityBanner } from "../../components/mca/McaEntityIndicators";
import { formatQuizTargetLicences } from "../../data/quizLicenses";
import { formatQuizTargetRoles } from "../../data/quizParticipants";

const apiEntity = "quiz";
export const QUIZ_SECTION_EVENT = "mbid-quiz-section";

export function requestQuizSection(id, section) {
  window.dispatchEvent(
    new CustomEvent(QUIZ_SECTION_EVENT, { detail: { id, section } })
  );
}

const AcknowledgementCell = ({ items = [] }) => {
  if (!items.length) {
    return (
      <Text size="sm" color="dimmed">
        --
      </Text>
    );
  }

  const showFileName = items.length > 1;

  return (
    <Stack spacing={6}>
      {items.map((item) => (
        <div key={item.documentId}>
          {showFileName && (
            <Text size="xs" lineClamp={1} title={item.fileName}>
              {item.fileName}
            </Text>
          )}
          <Text size="sm" color={item.acknowledgedAt ? undefined : "dimmed"}>
            {item.acknowledgedAt
              ? moment(item.acknowledgedAt).format("YYYY-MM-DD HH:mm")
              : "Not yet"}
          </Text>
        </div>
      ))}
    </Stack>
  );
};

const QuizDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const tabNav = useTabNavigate();
  const basePath = useQuizBasePath();
  const [api] = useServerApi();
  const [cellRender] = useCellRender();
  const [quiz, setQuiz] = useState(null);
  const [stats, setStats] = useState({});
  const [rows, setRows] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [resultFilter, setResultFilter] = useState("ALL");
  const [activeTab, setActiveTab] = useState(() =>
    new URLSearchParams(location.search).get("tab") === "result" ||
    location.pathname.endsWith("/results")
      ? "result"
      : "setting"
  );

  useEffect(() => {
    let active = true;
    const loadRoles = async () => {
      const res = await api.search({
        apiEntity: "userRole",
        searchQuery: {},
        currentPage: 1,
        pageSize: 200,
      });
      if (active) setUserRoles(res?.docs ?? []);
    };
    loadRoles();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      const [quiz, statsRes, resultsRes] = await Promise.all([
        api.getById({ apiEntity, id }),
        api.Quiz.getStatistics(id),
        api.Quiz.getResults(id),
      ]);
      setQuiz(quiz);
      setStats(statsRes?.data ?? {});
      setRows(resultsRes?.data ?? []);
    };
    load();
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("tab") === "result" || location.pathname.endsWith("/results")) {
      setActiveTab("result");
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onSection = (event) => {
      if (String(event.detail?.id) !== String(id)) return;
      setActiveTab(event.detail.section === "result" ? "result" : "setting");
    };
    window.addEventListener(QUIZ_SECTION_EVENT, onSection);
    return () => window.removeEventListener(QUIZ_SECTION_EVENT, onSection);
  }, [id]);

  const recentResults = useMemo(
    () =>
      rows
        .filter((r) => r.completedAt || r.result?.completedAt)
        .sort(
          (a, b) =>
            new Date(b.completedAt ?? b.result?.completedAt) -
            new Date(a.completedAt ?? a.result?.completedAt)
        )
        .slice(0, 10),
    [rows]
  );

  const filteredResults = useMemo(() => {
    if (resultFilter === "ALL") return rows;
    if (resultFilter === "PENDING") {
      return rows.filter((r) => !r.result || r.status === "NOT_STARTED");
    }
    return rows.filter((r) => r.status === resultFilter);
  }, [rows, resultFilter]);

  const resultCounts = useMemo(
    () => ({
      ALL: rows.length,
      PASSED: rows.filter((r) => r.status === "PASSED").length,
      FAILED: rows.filter((r) => r.status === "FAILED").length,
      PENDING: rows.filter((r) => !r.result || r.status === "NOT_STARTED").length,
    }),
    [rows]
  );

  if (!quiz) {
    return (
      <Page title="Quiz Detail">
        <Text color="dimmed">Loading...</Text>
      </Page>
    );
  }

  const roleLabel = formatQuizTargetRoles(quiz.targetRole, userRoles);

  const handleClose = () => {
    if (tabNav.enabled) {
      tabNav.closeActive();
      return;
    }
    navigate(basePath);
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    const params = new URLSearchParams(location.search);
    if (value === "result") params.set("tab", "result");
    else params.delete("tab");
    const nextSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (nextSearch === currentSearch) return;
    const pathname = location.pathname.replace(/\/results\/?$/, "");
    navigate(
      { pathname, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true }
    );
  };

  return (
    <Page
      title={
        <Group spacing="sm" align="center">
          <Title order={2}>{quiz.title}</Title>
          <QuizStatusBadge status={quiz.status} />
        </Group>
      }
      actions={
        <Group>
          {activeTab === "result" && (
            <Button
              variant="default"
              onClick={() =>
                api.Quiz.exportResults(id, `quiz-results-${quiz.code ?? id}.xlsx`)
              }
            >
              Export Results
            </Button>
          )}
          <Button onClick={() => navigate(`${basePath}/${id}/edit`)}>
            Edit Quiz
          </Button>
          <Button variant="default" onClick={handleClose}>{i18n.t("Close")}</Button>
        </Group>
      }
    >
      <McaEntityBanner entity={quiz} />
      <Tabs value={activeTab} onTabChange={handleTabChange}>
        <Tabs.List mb="md">
          <Tabs.Tab value="setting">{i18n.t("Setting")}</Tabs.Tab>
          <Tabs.Tab value="result">{i18n.t("Result")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="setting">
      <SimpleGrid cols={4} mb="lg">
        {[
          { label: "Total Participants", value: stats.total ?? 0 },
          { label: "Completed", value: stats.completed ?? 0 },
          { label: "Pending", value: stats.pending ?? 0 },
          { label: "Pass Rate", value: `${stats.passRate ?? 0}%` },
        ].map((card) => (
          <Paper key={card.label} p="md" withBorder>
            <Text size="xs" color="dimmed">
              {card.label}
            </Text>
            <Text size="xl" weight={700}>
              {card.value}
            </Text>
          </Paper>
        ))}
      </SimpleGrid>

      <SimpleGrid cols={2} mb="lg">
        <Paper p="md" withBorder>
          <Title order={5} mb="sm">
            Quiz Information
          </Title>
          <Stack spacing="xs">
            <Text size="sm">Start: {quiz.startDate ? moment(quiz.startDate).format("LL") : "--"}</Text>
            <Text size="sm">Deadline: {quiz.deadline ? moment(quiz.deadline).format("LL") : "--"}</Text>
            <Text size="sm">Passing Rate: {quiz.passingRate}%</Text>
            <Text size="sm">Questions: {quiz.questions?.length ?? 0}</Text>
            <Text size="sm">Duration: {quiz.testDuration > 0 ? `${quiz.testDuration} min` : "Unlimited"}</Text>
            <Text size="sm">Max Retake Count: {quiz.maxRetakeCount ?? 100}</Text>
          </Stack>
        </Paper>

        <Paper p="md" withBorder>
          <Title order={5} mb="sm">
            Target Participants
          </Title>
          <Text size="sm">Role: {roleLabel}</Text>
          <Text size="sm" mt="xs">
            Licences: {formatQuizTargetLicences(quiz)}
          </Text>
        </Paper>
      </SimpleGrid>

      <Paper p="md" withBorder mb="lg">
        <Title order={5} mb="sm">
          Required Documents
        </Title>
        {!quiz.documents?.length && (
          <Text size="sm" color="dimmed">
            No documents uploaded
          </Text>
        )}
        {(quiz.documents ?? []).map((doc) => (
          <Text key={doc._id} size="sm">
            {doc.fileName}
          </Text>
        ))}
      </Paper>

      <Paper p="md" withBorder>
        <Title order={5} mb="sm">
          Recent Participants
        </Title>
        <Table striped>
          <thead>
            <tr>
              <th>{i18n.t("Participant")}</th>
              <th>{i18n.t("Status")}</th>
              <th>{i18n.t("Try Count")}</th>
              <th>{i18n.t("Score")}</th>
            </tr>
          </thead>
          <tbody>
            {recentResults.map((row) => (
              <tr key={row.user?._id}>
                <td>{cellRender.User(row.user)}</td>
                <td>
                  <QuizStatusBadge status={row.status} />
                </td>
                <td>
                  {row.attemptCount ?? 0} / {quiz.maxRetakeCount ?? 100}
                </td>
                <td>{row.score ?? "--"}</td>
              </tr>
            ))}
            {!recentResults.length && (
              <tr>
                <td colSpan={4}>
                  <Text color="dimmed" size="sm">
                    No completions yet
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Paper>
        </Tabs.Panel>

        <Tabs.Panel value="result">
          <SimpleGrid cols={4} mb="lg">
            {[
              { label: "Passed", value: `${stats.passed ?? 0} (${stats.passRate ?? 0}%)` },
              { label: "Failed", value: stats.failed ?? 0 },
              { label: "In Progress", value: stats.inProgress ?? 0 },
              { label: "Average Score", value: `${stats.averageScore ?? 0}%` },
            ].map((card) => (
              <Paper key={card.label} p="md" withBorder>
                <Text size="xs" color="dimmed">
                  {card.label}
                </Text>
                <Text size="xl" weight={700}>
                  {card.value}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>

          <Tabs value={resultFilter} onTabChange={setResultFilter} mb="md">
            <Tabs.List>
              <Tabs.Tab value="ALL">All ({resultCounts.ALL})</Tabs.Tab>
              <Tabs.Tab value="PASSED">Passed ({resultCounts.PASSED})</Tabs.Tab>
              <Tabs.Tab value="FAILED">Failed ({resultCounts.FAILED})</Tabs.Tab>
              <Tabs.Tab value="PENDING">Pending ({resultCounts.PENDING})</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          {(quiz.documents ?? []).length === 1 && (
            <Text size="sm" color="dimmed" mb="sm">
              Document: {quiz.documents[0].fileName}
            </Text>
          )}

          <div style={{ overflowX: "auto" }}>
          <Table
            striped
            highlightOnHover
            withBorder
            sx={{
              "thead th": { whiteSpace: "nowrap" },
            }}
          >
            <thead>
              <tr>
                <th>{i18n.t("Participant")}</th>
                <th>{i18n.t("License Type")}</th>
                <th>{i18n.t("Score")}</th>
                <th>{i18n.t("Status")}</th>
                <th>{i18n.t("Try Count")}</th>
                <th>{i18n.t("Test Completed At")}</th>
                <th>{i18n.t("Document Acknowledged At")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredResults.map((row) => (
                <tr key={row.user?._id ?? row.user}>
                  <td>{cellRender.User(row.user)}</td>
                  <td>{row.licenceType}</td>
                  <td>
                    <Text
                      color={
                        row.status === "PASSED"
                          ? "green"
                          : row.status === "FAILED"
                            ? "red"
                            : undefined
                      }
                    >
                      {row.score != null ? `${row.score}%` : "--"}
                    </Text>
                  </td>
                  <td>
                    <QuizStatusBadge status={row.status} />
                  </td>
                  <td>
                    {row.attemptCount ?? 0} / {quiz.maxRetakeCount ?? 100}
                  </td>
                  <td>
                    {row.completedAt
                      ? moment(row.completedAt).format("YYYY-MM-DD HH:mm")
                      : "--"}
                  </td>
                  <td>
                    <AcknowledgementCell items={row.acknowledgements} />
                  </td>
                </tr>
              ))}
              {!filteredResults.length && (
                <tr>
                  <td colSpan={7}>
                    <Text color="dimmed" size="sm">
                      No result records
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          </div>
        </Tabs.Panel>
      </Tabs>
    </Page>
  );
};

export default QuizDetail;
